import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Card, Input, Select, Modal, StatusPill, EmptyState, useToast } from '../../../design-system';
import AgentInstallHelp from './AgentInstallHelp';
import '../../styles/Credentials.css';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// Expiry banner: warn within 30 days, danger once past.
const expiryPill = (expiresAt) => {
  if (!expiresAt) return null;
  const days = Math.ceil((new Date(expiresAt) - Date.now()) / 86400000);
  if (days < 0) return <StatusPill variant="danger" label={`Expired ${fmtDate(expiresAt)}`} />;
  if (days <= 30) return <StatusPill variant="warning" label={`Expires in ${days}d`} />;
  return null;
};

const REVEAL_SECONDS = 30;

// Store, view and use clients' government-portal logins. Passwords are stored
// encrypted server-side and NEVER returned except through a step-up "Reveal".
// A revealed value lives only in transient state here — never in localStorage,
// Redux, or anywhere it could persist — and auto-hides after 30s.
//
// Two modes, one component:
//   clientId given  → the per-client card on the client edit page (add is fixed
//                     to that client, no client column).
//   clientId absent → the org-wide vault cockpit (/org-admin/credentials): lists
//                     every client's logins, filterable, add picks the client.
function CredentialsManager({ clientId = null }) {
  const allView = !clientId;
  const toast = useToast();

  const [credentials, setCredentials] = useState([]);
  const [portals, setPortals] = useState([]);
  const [clients, setClients] = useState([]); // only used in the all-view
  const [filterClientId, setFilterClientId] = useState('');
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('active'); // 'active' | 'deleted'

  // Add / edit form. editingId = null → add; an id → editing that credential.
  const [addOpen, setAddOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ clientId: '', portalId: '', label: '', username: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState('');

  // Reveal (step-up)
  const [revealFor, setRevealFor] = useState(null);
  const [stepUpPassword, setStepUpPassword] = useState('');
  const [revealed, setRevealed] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [revealError, setRevealError] = useState('');
  const [countdown, setCountdown] = useState(REVEAL_SECONDS);

  // Login / agent
  const [loginBusy, setLoginBusy] = useState(null);
  const [installOpen, setInstallOpen] = useState(false);
  // The credential the user pressed Login on before we discovered this machine
  // has no agent — resumed automatically once setup finishes.
  const [pendingLogin, setPendingLogin] = useState(null);
  // The in-flight "is the agent actually handling this order?" poll — kept so
  // a second Login click, or an unmount, can cancel a stale one rather than
  // leaving it to fire setInstallOpen against a click nobody cares about anymore.
  const pollTimer = useRef(null);
  useEffect(() => () => clearTimeout(pollTimer.current), []);

  // Portals (and, in the all-view, the client list) are fetched once.
  useEffect(() => {
    apiClient.get('/api/portals').then((r) => setPortals(r.data)).catch(() => {});
    // Lean picker list (id, displayName, clientCode) — not the full client docs.
    if (allView) apiClient.get('/api/clients/options').then((r) => setClients(r.data)).catch(() => {});
  }, [allView]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const scope = clientId || filterClientId;
      const params = {};
      if (scope) params.clientId = scope;
      if (view === 'deleted') params.deleted = 'true';
      const res = await apiClient.get('/api/credentials', { params });
      setCredentials(res.data);
    } catch {
      toast.danger('Failed to load credentials');
    } finally {
      setLoading(false);
    }
    // ⚠️ `toast` IS DELIBERATELY NOT A DEPENDENCY — `load` is called from an
    // effect, so anything that changes its identity re-fetches. Keying it on
    // the notification its own failure raises is a retry loop waiting for an
    // outage. See the note in design-system/Toast.jsx.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, filterClientId, view]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-hide a revealed secret after REVEAL_SECONDS.
  useEffect(() => {
    if (!revealed) return undefined;
    setCountdown(REVEAL_SECONDS);
    const tick = setInterval(() => setCountdown((c) => c - 1), 1000);
    const hide = setTimeout(() => closeReveal(), REVEAL_SECONDS * 1000);
    return () => {
      clearInterval(tick);
      clearTimeout(hide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealed]);

  const openAdd = () => {
    setEditingId(null);
    setForm({
      clientId: clientId || filterClientId || '',
      portalId: portals[0]?._id || '',
      label: '',
      username: '',
      password: '',
    });
    setAddError('');
    setAddOpen(true);
  };

  // Edit reuses the same modal. Client and portal are fixed (the secret is
  // encrypted to that client/owner, so re-homing it isn't an edit) — shown
  // read-only. Username/password are left BLANK: the backend re-encrypts only
  // the fields you actually send, so blank = keep the current secret.
  const openEdit = (cred) => {
    setEditingId(cred.id);
    setForm({
      clientId: cred.clientId || cred.client?.id || '',
      portalId: cred.portalId || cred.portal?.id || '',
      label: cred.label || '',
      username: '',
      password: '',
    });
    setAddError('');
    setAddOpen(true);
  };

  const closeAddEdit = () => {
    setAddOpen(false);
    setEditingId(null);
  };

  const saveCredential = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) return setAddError('Label is required');
    setSaving(true);
    setAddError('');
    try {
      if (editingId) {
        // Only send secrets the user re-typed; blank keeps the stored value.
        const payload = { label: form.label.trim() };
        if (form.username) payload.username = form.username;
        if (form.password) payload.password = form.password;
        await apiClient.put(`/api/credentials/${editingId}`, payload);
        toast.success('Credential updated');
      } else {
        const targetClient = clientId || form.clientId;
        if (!targetClient) return setAddError('Pick a client');
        if (!form.portalId) return setAddError('Pick a portal');
        if (!form.username || !form.password) return setAddError('Username and password are required');
        await apiClient.post('/api/credentials', {
          clientId: targetClient,
          portalId: form.portalId,
          label: form.label.trim(),
          username: form.username,
          password: form.password,
        });
        toast.success('Credential saved');
      }
      closeAddEdit();
      await load();
    } catch (err) {
      setAddError(err.response?.data?.error || 'Failed to save credential');
    } finally {
      setSaving(false);
    }
  };

  const removeCredential = async (cred) => {
    // Soft delete only — the row and its ciphertext stay; it's recoverable from
    // the Deleted view. Say so, so a delete doesn't read as permanent.
    if (!window.confirm(`Move the ${cred.portal?.displayName || 'portal'} login "${cred.label}" to Deleted? You can restore it later.`)) return;
    try {
      await apiClient.delete(`/api/credentials/${cred.id}`);
      toast.success('Credential moved to Deleted');
      await load();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to delete');
    }
  };

  const restoreCredential = async (cred) => {
    try {
      await apiClient.patch(`/api/credentials/${cred.id}/restore`);
      toast.success('Credential restored');
      await load();
    } catch (err) {
      // 409 when a live login with the same label already exists.
      toast.danger(err.response?.data?.error || 'Failed to restore');
    }
  };

  const openReveal = (cred) => {
    setRevealFor(cred);
    setStepUpPassword('');
    setRevealed(null);
    setRevealError('');
  };

  const closeReveal = () => {
    setRevealFor(null);
    setStepUpPassword('');
    setRevealed(null);
    setRevealError('');
  };

  const doReveal = async (e) => {
    e.preventDefault();
    setRevealing(true);
    setRevealError('');
    try {
      const res = await apiClient.post(`/api/credentials/${revealFor.id}/reveal`, { password: stepUpPassword });
      setRevealed(res.data);
      setStepUpPassword('');
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) setRevealError('Too many reveals — try again later.');
      else if (status === 403) setRevealError('Wrong password. Re-enter to confirm it’s you.');
      else setRevealError(err.response?.data?.error || 'Reveal failed');
    } finally {
      setRevealing(false);
    }
  };

  // Launch the local agent via the custom scheme, then confirm it actually
  // picked up the order.
  //
  // ⚠️ USED TO detect this from a `blur` event on the theory that focus only
  // leaves the page when an external app opens. That has a false positive
  // this codebase hit in practice: when NO handler is registered for
  // hrms-agent://, Chrome/Edge show their own "Get an app to open this link"
  // dialog — which ALSO blurs the window. A machine with no agent at all was
  // therefore indistinguishable from one that had just launched it, so the
  // install prompt never appeared and the user was left staring at that OS
  // dialog with no way back into the flow.
  //
  // The real signal is the order's own outcome: the agent reports 'started'
  // (see agent/src/index.ts) the moment it verifies the ticket, well before
  // runOrder — which can sit at a human captcha step for minutes — so polling
  // only needs a few seconds' patience, not the whole login.
  const launchAgent = (ticket, jti) => {
    window.location.href = `hrms-agent://run?ticket=${ticket}`;

    clearTimeout(pollTimer.current);
    const deadline = Date.now() + 8000;
    const poll = async () => {
      if (Date.now() > deadline) {
        setInstallOpen(true);
        return;
      }
      try {
        const { data } = await apiClient.get(`/api/credentials/orders/${jti}/status`);
        if (data.outcome !== 'issued') {
          // The agent picked it up (or already failed on its own, which shows
          // its own native dialog) — either way there's nothing left to resume.
          setPendingLogin(null);
          return;
        }
      } catch {
        /* a blip mid-poll isn't the failure; the deadline is */
      }
      pollTimer.current = setTimeout(poll, 750);
    };
    pollTimer.current = setTimeout(poll, 750);
  };

  const login = async (cred) => {
    setLoginBusy(cred.id);
    try {
      const res = await apiClient.post(`/api/credentials/${cred.id}/order`, {});
      // ⚠️ Armed BEFORE launching, not just in the `no_agent` catch below.
      // The server believing an agent exists doesn't mean THIS machine can
      // actually reach it — the launcher registration can point at a copy
      // that moved or was removed. That failure only shows up client-side, as
      // launchAgent's own poll timing out, which used to open the exact same
      // install dialog with pendingLogin left unset: setup would finish and
      // nothing resumed, silently dropping the login the user actually asked
      // for. launchAgent clears this the moment the poll confirms the agent
      // has the order.
      setPendingLogin(cred);
      launchAgent(res.data.ticket, res.data.jti);
      toast.info('Opening the login agent…');
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'no_agent') {
        // Remember WHAT they were trying to log into. Setup is a detour, not the
        // task — once the agent is ready we finish the job they actually asked
        // for instead of returning them to a list to start again.
        setPendingLogin(cred);
        setInstallOpen(true);
      } else if (code === 'no_recipe') toast.info(err.response?.data?.error || 'Auto-login not available for this portal yet.');
      else toast.danger(err.response?.data?.error || 'Could not start login');
    } finally {
      setLoginBusy(null);
    }
  };

  // Called when the install dialog sees this machine register itself.
  const resumePendingLogin = useCallback(() => {
    const cred = pendingLogin;
    if (!cred) return;
    setPendingLogin(null);
    // A beat so the user reads the "Ready" line before the dialog closes and
    // Chrome takes focus — otherwise the whole setup appears to have done
    // nothing and a browser window simply appears.
    setTimeout(() => {
      setInstallOpen(false);
      login(cred);
    }, 1200);
    // `login` is redefined on every render and depends on nothing that changes
    // the behaviour here; including it would re-create this callback constantly
    // and restart the install dialog's effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingLogin]);

  const addButton = (
    <Button type="button" variant="primary" size="sm" onClick={openAdd} disabled={loading}>
      + Add Login
    </Button>
  );

  const viewToggle = (
    <div className="cred-view-toggle">
      <Button type="button" size="sm" variant={view === 'active' ? 'primary' : 'secondary'} onClick={() => setView('active')}>
        Active
      </Button>
      <Button type="button" size="sm" variant={view === 'deleted' ? 'primary' : 'secondary'} onClick={() => setView('deleted')}>
        Deleted
      </Button>
    </div>
  );

  return (
    <Card>
      <Card.Header
        title={allView ? 'Credentials' : 'Portal Logins'}
        subtitle={
          allView
            ? 'Stored government-portal logins across all clients'
            : 'Stored government-portal credentials for this client'
        }
        action={
          <div className="cred-header-controls">
            {allView && (
              <Select
                aria-label="Filter by client"
                value={filterClientId}
                onChange={(e) => setFilterClientId(e.target.value)}
              >
                <option value="">All clients</option>
                {clients.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.displayName}
                  </option>
                ))}
              </Select>
            )}
            {viewToggle}
            {view === 'active' && addButton}
          </div>
        }
      />

      {loading ? (
        <p className="cred-hint">Loading…</p>
      ) : credentials.length === 0 ? (
        view === 'deleted' ? (
          <EmptyState icon="🗑️" title="Nothing deleted" description="Deleted portal logins land here and can be restored." />
        ) : (
          <EmptyState
            icon="🔐"
            title="No portal logins stored"
            description={
              allView
                ? "Store a client's GST or other portal login to auto-fill it at sign-in time."
                : "Store this client's GST or other portal login to auto-fill it at sign-in time."
            }
            action={addButton}
          />
        )
      ) : (
        <div className="cred-list">
          {credentials.map((cred) => (
            <div key={cred.id} className="cred-row">
              <div className="cred-identity">
                <span className="cred-portal">
                  {cred.portal?.displayName || cred.portal?.code || 'Portal'}
                  {allView && cred.client?.name ? <span className="cred-client"> · {cred.client.name}</span> : null}
                </span>
                <span className="cred-label">{cred.label}</span>
              </div>

              <span className="cred-masked" aria-label="password hidden">
                {cred.maskedPassword}
              </span>

              <span className="cred-meta">
                {expiryPill(cred.expiresAt)}
                <span className="cred-lastused">Last used {fmtDate(cred.lastUsedAt)}</span>
              </span>

              <div className="cred-actions">
                {view === 'deleted' ? (
                  <Button type="button" variant="primary" size="sm" onClick={() => restoreCredential(cred)}>
                    Restore
                  </Button>
                ) : (
                  <>
                    <Button type="button" variant="primary" size="sm" loading={loginBusy === cred.id} onClick={() => login(cred)}>
                      Login
                    </Button>
                    <Button type="button" variant="secondary" size="sm" onClick={() => openReveal(cred)}>
                      Reveal
                    </Button>
                    <Button type="button" variant="secondary" size="sm" onClick={() => openEdit(cred)}>
                      Edit
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeCredential(cred)}>
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit */}
      {addOpen && (
        <Modal
          isOpen
          onClose={closeAddEdit}
          title={editingId ? 'Edit portal login' : 'Add portal login'}
          footer={
            <>
              <Button type="button" variant="secondary" onClick={closeAddEdit}>
                Cancel
              </Button>
              <Button type="button" variant="primary" onClick={saveCredential} loading={saving}>
                {editingId ? 'Save changes' : 'Save'}
              </Button>
            </>
          }
        >
          <form className="cred-form" onSubmit={saveCredential}>
            {allView && (
              <Select
                label="Client"
                value={form.clientId}
                onChange={(e) => setForm({ ...form, clientId: e.target.value })}
                disabled={!!editingId}
                helperText={editingId ? 'Client cannot be changed on an existing login' : undefined}
              >
                <option value="">— Select a client —</option>
                {clients.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.displayName}
                  </option>
                ))}
              </Select>
            )}
            <Select
              label="Portal"
              value={form.portalId}
              onChange={(e) => setForm({ ...form, portalId: e.target.value })}
              disabled={!!editingId}
              helperText={editingId ? 'Portal cannot be changed on an existing login' : undefined}
            >
              <option value="">— Select a portal —</option>
              {portals.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.displayName}
                </option>
              ))}
            </Select>
            <Input
              label="Label"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. GSTIN 27ABCDE… — primary"
              helperText="How you'll recognise this login (e.g. which GSTIN)"
            />
            <Input
              label="Username"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              autoComplete="off"
              placeholder={editingId ? 'Leave blank to keep current' : undefined}
            />
            <Input
              label="Password"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              autoComplete="new-password"
              placeholder={editingId ? 'Leave blank to keep current' : undefined}
            />
            {editingId && (
              <p className="cred-hint">
                Username and password are hidden for security. Fill them only to replace the stored value.
              </p>
            )}
            {addError && <p className="cred-form-error">{addError}</p>}
          </form>
        </Modal>
      )}

      {/* Reveal (step-up) */}
      {revealFor && (
        <Modal
          isOpen
          onClose={closeReveal}
          title={`Reveal — ${revealFor.portal?.displayName || 'login'}`}
          footer={
            revealed ? (
              <Button type="button" variant="secondary" onClick={closeReveal}>
                Hide
              </Button>
            ) : (
              <>
                <Button type="button" variant="secondary" onClick={closeReveal}>
                  Cancel
                </Button>
                <Button type="button" variant="primary" onClick={doReveal} loading={revealing}>
                  Reveal
                </Button>
              </>
            )
          }
        >
          {revealed ? (
            <div className="cred-reveal">
              <p className="cred-reveal-note">Auto-hides in {countdown}s. Do not screenshot or paste elsewhere.</p>
              <div className="cred-reveal-field">
                <span>Username</span>
                <code>{revealed.username}</code>
              </div>
              <div className="cred-reveal-field">
                <span>Password</span>
                <code>{revealed.password}</code>
              </div>
            </div>
          ) : (
            <form className="cred-form" onSubmit={doReveal}>
              <p className="cred-hint">Re-enter your own password to confirm it’s you. This reveal is logged.</p>
              <Input
                label="Your password"
                type="password"
                value={stepUpPassword}
                onChange={(e) => setStepUpPassword(e.target.value)}
                autoComplete="current-password"
                autoFocus
              />
              {revealError && <p className="cred-form-error">{revealError}</p>}
            </form>
          )}
        </Modal>
      )}

      {/* Agent not installed — download it, for THIS computer's OS. */}
      {installOpen && (
        <AgentInstallHelp
          onClose={() => {
            setInstallOpen(false);
            setPendingLogin(null);
          }}
          // Setup finishing is not what the user asked for — they pressed Login.
          // Resume that rather than making them start again.
          onReady={resumePendingLogin}
        />
      )}
    </Card>
  );
}

export default CredentialsManager;
